"use client";
import { useRouter } from "@commerce-frontend/i18n/navigation";
import type { Locale } from "@commerce-frontend/i18n/types";
import type { Money } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Skeleton } from "@commerce-frontend/ui/base/Skeleton/Skeleton";
import {
	Carousel,
	CarouselContent,
	CarouselNext,
	CarouselPrevious,
} from "@commerce-frontend/ui/components/Carousel/Carousel";
import {
	ProductCard,
	ProductCardImage,
	ProductCardInfo,
	ProductCardInfoPrice,
	ProductCardInfoProductDescription,
	ProductCardInfoProductName,
} from "@commerce-frontend/ui/components/ProductCard/ProductCard";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import { BREAKPOINTS } from "@commerce-frontend/ui/tailwind.config";
import { range } from "@labdigital/toolkit";
import { useLocale, useTranslations } from "next-intl";
import { useTransition } from "react";
import { useHits, useInstantSearch } from "react-instantsearch";
import { Image, imagePlaceholder } from "~/components/Image/Image";
import { PriceTag } from "~/components/PriceTag/PriceTag";
import { productDetailHref } from "~/lib/link-builder";
import { useMenu } from "../../../MenuContext";

type Props = {
	query: string;
};

type ProductHit = {
	objectID: string;
	name: Record<Locale, string>;
	slug: Record<Locale, string>;
	image?: string;
	brand?: Record<Locale, string>;
	rawMaterial?: Record<Locale, string>;
	neckline?: Record<Locale, string>;
	fit?: Record<Locale, string>;
	denier?: Record<Locale, string>;
	packSize?: number;
	startingPrice?: Money;
};

export const SearchProducts = ({ query }: Props) => {
	const { items, results } = useHits<ProductHit>();
	const t = useTranslations("SearchProducts");
	const { status } = useInstantSearch();
	const { setSearchOpen } = useMenu();

	if (!items.length && results?.query && status === "idle") {
		return;
	}

	return (
		<div className="grow">
			<div className="mb-xs flex gap-xs h-11 items-center py-sm text-gray-700">
				{t.rich("title", {
					bold: (chunks) => <span className="font-bold">{chunks}</span>,
					search: query,
				})}
			</div>

			{(!items.length && !results?.query) || status === "loading" ? (
				<div className="flex basis-52 gap-xl lg:basis-1/4">
					{range({ start: 0, end: 4 }).map((item) => (
						<div key={item}>
							<Skeleton
								type="image"
								className="aspect-square w-[40%] min-w-[150px] max-w-[220px] shrink-0 rounded-lg lg:w-52"
							/>
							<Skeleton type="text" textLines={1} className="lg:mt-8" />
							<Skeleton type="text" textLines={3} />
						</div>
					))}
				</div>
			) : (
				<>
					<div className="hidden gap-xl overflow-hidden lg:flex">
						<Products
							items={items}
							className="flex shrink-0 flex-col w-[calc(25%-1rem)] max-w-[220px]"
						/>
					</div>

					<Carousel className="mb-lg grow overflow-hidden lg:hidden" opts={{ dragFree: true }}>
						<div className="relative">
							<CarouselContent>
								<Products
									items={items}
									className="flex shrink-0 flex-col w-[40%] min-w-[150px] max-w-[220px]"
								/>
							</CarouselContent>
							<CarouselNext />
							<CarouselPrevious />
						</div>
					</Carousel>

					{results?.nbHits ? (
						<Button
							onClick={() => setSearchOpen(false)}
							href={{ pathname: "/search", query: { q: query } }}
							variant="primary"
							size="lg"
						>
							{t("link", {
								count: results?.nbHits,
							})}
						</Button>
					) : null}
				</>
			)}
		</div>
	);
};

type ProductsProps = {
	className?: string;
	items?: ProductHit[];
};

const Products = ({ className, items }: ProductsProps) => {
	const tProductCard = useTranslations("ProductBuySection");
	const locale = useLocale() as Locale;
	const { setSearchOpen } = useMenu();
	const [_, startTransition] = useTransition();
	const router = useRouter();

	return items?.map((item) => {
		const neckline = item?.neckline?.[locale] ?? "";
		const rawmaterial = item?.rawMaterial?.[locale] ?? "";
		const brand = item?.brand?.[locale] ?? "";
		const denier = item?.denier?.[locale] ?? "";
		const fit = item?.fit?.[locale] ?? "";
		const packsize = item?.packSize ? tProductCard("pack-size", { size: item.packSize }) : "";

		const underline = [brand, rawmaterial, denier, neckline, fit, packsize]
			.filter(Boolean)
			.join(", ");

		return (
			<Link
				className={className}
				key={`search-product-${item.objectID}`}
				href={productDetailHref(item.slug[locale])}
				onClick={() => {
					startTransition(() => {
						router.push(
							{
								pathname: "/product/[slug]",
								params: {
									slug: item.slug[locale],
								},
							},
							{ locale },
						);
						setSearchOpen(false);
					});
				}}
			>
				<ProductCard
					aria-label={item.name[locale]}
					aria-describedby={`product-card-${item.slug[locale]}`}
				>
					<ProductCardImage>
						<Image
							src={item.image ?? imagePlaceholder}
							alt={item.name[locale]}
							width={212}
							height={212}
							sizes={`(min-width: ${BREAKPOINTS.lg}) 20vw, (min-width: ${BREAKPOINTS.xl}) 212px, 50vw`}
							className="w-full object-fill"
						/>
					</ProductCardImage>
					<ProductCardInfo>
						<ProductCardInfoPrice>
							{item.startingPrice?.centAmount ? (
								<PriceTag size="xs" price={item.startingPrice} locale={locale} />
							) : null}
						</ProductCardInfoPrice>
						<ProductCardInfoProductName>{item.name[locale]}</ProductCardInfoProductName>
						<ProductCardInfoProductDescription>{underline}</ProductCardInfoProductDescription>
					</ProductCardInfo>
				</ProductCard>
			</Link>
		);
	});
};
